import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'

interface CounterProps {
  end: number
  duration?: number
  suffix?: string
  prefix?: string
  className?: string
}

export function Counter({ end, duration = 2, suffix = '', prefix = '', className = '' }: CounterProps) {
  const [count, setCount] = useState(0)
  const counterRef = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)
  const prefersReducedMotion = useRef(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    prefersReducedMotion.current = mediaQuery.matches
  }, [])

  useEffect(() => {
    const element = counterRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true

            if (prefersReducedMotion.current) {
              setCount(end)
            } else {
              const obj = { value: 0 }
              gsap.to(obj, {
                value: end,
                duration: duration,
                ease: 'power2.out',
                onUpdate: () => {
                  setCount(Math.floor(obj.value))
                },
              })
            }
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [end, duration])

  const formatNumber = (num: number) => {
    if (num >= 100000000) {
      return (num / 100000000).toFixed(0) + '亿'
    }
    if (num >= 10000) {
      return (num / 10000).toFixed(0) + '万'
    }
    return num.toLocaleString()
  }

  return (
    <span ref={counterRef} className={className}>
      {prefix}{formatNumber(count)}{suffix}
    </span>
  )
}
